import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { Message } from 'primeng/message';
import { ProgressBarModule } from 'primeng/progressbar';
import { Textarea } from 'primeng/textarea';
import { Tooltip } from 'primeng/tooltip';
import FileUpload from '../../../../../../types/media/FileUpload';
import { UploadUseCase } from '../../../../../../types/shared/media/UploadUseCase';
import { IsMaintainerDirective } from '../../../../directives/is-maintainer.directive';
import { MediaService } from '../../../../services/media.service';

@Component({
    selector: 'app-upload-media',
    templateUrl: './upload-media.component.html',
    styleUrls: [ './upload-media.component.scss' ],
    imports: [
        ProgressBarModule,
        ButtonModule,
        FormsModule,
        Message,
        Tooltip,
        IftaLabelModule,
        Textarea,
        IsMaintainerDirective
    ]
})
export class UploadMediaComponent {
    [x: string]: any;

    selectedFiles: FileList | null = null;
    progress: number = 0;
    uploadId: string | undefined = undefined;
    uploadCompleted: boolean = false;
    uploadInProgress: boolean = false;
    mediaNotes: string = '';

    @Input() useCase: UploadUseCase = UploadUseCase.NormalUpload;
    @Input() mediaId: number | undefined = undefined;
    @Output() uploadSuccess = new EventEmitter<number>();
    @Output() uploadError = new EventEmitter<void>();

    constructor(private mediaService: MediaService, private messageService: MessageService) {
    }

    onFileChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input?.files) {
            this.selectedFiles = input.files;
            this.uploadCompleted = false;
        }
    }

    get UploadUseCase() {
        return UploadUseCase;
    }

    hasSelectedFiles(): boolean {
        return this.selectedFiles !== null && this.selectedFiles.length > 0;
    }

    async startUpload(): Promise<void> {
        const files = this.selectedFiles ?? [];
        if (files.length === 0) {
            this.messageService.add({
                severity: 'error',
                summary: 'No files selected',
                detail: 'Please select a file to upload.',
                life: 3000,
            });
            return;
        }

        const file = files[0];
        const fileUpload: FileUpload = {
            uuid: undefined,
            filename: file.name,
            description: this.mediaNotes || '',
            contentType: file.type,
            size: file.size,
        };

        this.uploadInProgress = true;
        this.uploadCompleted = false;

        try {
            const response = await this.mediaService.initUpload(fileUpload);
            this.uploadId = response.uuid;
            await this.uploadParts(file);
            let mediaId = await this.completeUpload();

            this.messageService.add({
                severity: 'success',
                summary: 'Upload Successful',
                detail: 'Your file has been uploaded successfully.',
                life: 3000,
            });

            this.uploadSuccess.emit(mediaId);
        } catch (error: any) {
            this.messageService.add({
                severity: 'error',
                summary: 'Upload Failed',
                detail: error.message,
                life: 5000,
            });
            this.uploadError.emit();
        } finally {
            this.uploadInProgress = false;
        }
    }

    async uploadParts(file: File): Promise<void> {
        const chunkSize = 5 * 1024 * 1024; // 5MB
        const totalParts = Math.ceil(file.size / chunkSize);

        for (let part = 0; part < totalParts; part++) {
            const start = part * chunkSize;
            const end = Math.min(start + chunkSize, file.size);
            const partBlob = file.slice(start, end);

            if (this.uploadId) {
                await this.mediaService.uploadPart(this.uploadId, part, partBlob);
                this.progress = Math.round(( ( part + 1 ) / totalParts ) * 100);
            }
        }
    }

    async completeUpload(): Promise<number> {
        if (this.uploadId) {
            let mediaId: number | undefined;
            if (this.useCase === UploadUseCase.ReplaceMediaContent && this.mediaId) {
                mediaId = await this.mediaService.completeUpload(this.uploadId, this.mediaId.toString());
            } else {
                mediaId = await this.mediaService.completeUpload(this.uploadId);
            }
            this.uploadCompleted = true;
            this.uploadId = undefined;
            this.selectedFiles = null;
            this.mediaNotes = '';
            return mediaId;
        } else {
            throw new Error('No upload ID available to complete the upload.');
        }
    }

    async cancelUpload(): Promise<void> {
        if (this.uploadId) {
            try {
                await this.mediaService.cancelUpload(this.uploadId);
                this.uploadId = undefined;
                this.progress = 0;
                this.uploadInProgress = false;
                this.messageService.add({
                    severity: 'info',
                    summary: 'Upload Canceled',
                    detail: 'The upload has been canceled.',
                    life: 3000,
                });
            } catch (error) {
                console.error('Error cancelling the upload:', error);
            }
        }
    }
}
